import { Controller } from "@hotwired/stimulus"

/**
 * The overlay the editor wears while it starts.
 *
 * It only moves a POINTER along a list the server rendered. Every word, icon
 * and colour is in the ERB — same trade `Floorplans::SaveIndicatorComponent`
 * makes for its four states, and the reason is the same: otherwise ten locales
 * of copy would live in a Stimulus controller.
 */
export default class extends Controller {
  static targets = ["spinner", "tick", "subtitle"]
  static values = { steps: Array }

  connect() {
    // RESET, not "start if not started". Turbo caches the snapshot with this
    // overlay already removed, and a restore builds a fresh controller over a
    // fresh canvas that genuinely remounts — so a restored page must show this
    // again or it is a blank stage with no explanation. v1 cannot have this bug
    // because Inertia rebuilds its tree.
    this.at = 0
    this.finished = false
    this.element.hidden = false
    this.element.style.opacity = ""
    this.#paint()

    // THE LONG STOP, which v1 deliberately does not have — and the difference
    // is what each overlay covers. v1's sits over a canvas that is useless
    // without the engine; this one sits over a server-rendered layer tree and
    // footer that work with no engine at all.
    // A wedged overlay here blocks working UI, so it gives up rather than
    // holding the page.
    //
    // Everything the editor can say about a failure it already says in the
    // status line, so there is nothing to add here beyond getting out of the way.
    this.ceiling = setTimeout(() => this.#finish(), CEILING_MS)
  }

  disconnect() {
    clearTimeout(this.ceiling)
    clearTimeout(this.pending)
    clearTimeout(this.fade)
    clearTimeout(this.removal)
  }

  /**
   * data-action="canvas:boot-step@document->floorplans--canvas-loading#advance"
   */
  advance(event) {
    const step = event?.detail?.step
    if (step === undefined) return

    const target = this.stepsValue.indexOf(step)
    if (target < 0) return

    this.#advanceTo(target)
  }

  /**
   * data-action="canvas:boot-failed@document->floorplans--canvas-loading#abandon"
   *
   * The chunk would not load, or the mount threw. The canvas controller has
   * already written the failure into the editor's status line; this must not
   * outlive it, or the reader is left with a spinner over a page that has
   * stopped trying.
   */
  abandon() {
    if (this.finished) return
    this.finished = true

    clearTimeout(this.ceiling)
    clearTimeout(this.pending)

    // Gone at once, and WITHOUT the finish: no tick, no "ready", no steps
    // painted done. Nothing loaded, and the status line already says why —
    // half a second of a success state over it would say the opposite.
    this.element.remove()
  }

  /**
   * NEVER BACKWARDS, and at most ONE step per call.
   *
   * The one-step rule is load-bearing rather than cosmetic. `subsets:load` —
   * the `canvas` milestone — is emitted only `if (data.groups)`, and on the
   * `#loadContentFromApi` catch path a floorplan falls back to a null content
   * and it never fires at all. Skipping forward by one means the ladder heals
   * on the next milestone instead of stranding the reader on step two.
   */
  #advanceTo(target) {
    if (this.finished || target <= this.at) return

    const next = Math.min(this.at + 1, target)

    // A minimum dwell, so a fast boot is a sequence somebody can read rather
    // than a flicker. Deferred rather than dropped: the pointer has already
    // moved, and the step after this one is queued behind the same timer.
    const elapsed = Date.now() - (this.shownAt ?? 0)
    if (elapsed < MIN_STEP_MS) {
      clearTimeout(this.pending)
      this.pending = setTimeout(
        () => this.#advanceTo(target),
        MIN_STEP_MS - elapsed,
      )
      return
    }

    this.at = next
    this.#paint()

    if (this.at >= this.stepsValue.length - 1) {
      this.#finish()
      return
    }

    if (this.at < target) this.#advanceTo(target)
  }

  #paint() {
    this.shownAt = Date.now()

    for (const [index, step] of this.stepsValue.entries()) {
      const row = this.element.querySelector(`[data-loading-step='${step}']`)
      if (!row) continue

      const status =
        index < this.at ? "done" : index === this.at ? "active" : "pending"

      row.hidden = status === "pending"
      row.dataset.status = status

      for (const icon of row.querySelectorAll("[data-step-icon]")) {
        icon.hidden =
          icon.dataset.stepIcon !== (status === "done" ? "done" : "pending")
      }

      const bar = row.querySelector("[data-step-bar]")
      if (bar) bar.hidden = status !== "active"
    }
  }

  /** Every step done, the header says so, and then it goes. */
  #finish() {
    if (this.finished) return
    this.finished = true

    clearTimeout(this.ceiling)
    clearTimeout(this.pending)

    this.at = this.stepsValue.length
    this.#paint()

    if (this.hasSpinnerTarget) this.spinnerTarget.hidden = true
    if (this.hasTickTarget) this.tickTarget.hidden = false
    if (this.hasSubtitleTarget) {
      this.subtitleTarget.textContent =
        this.subtitleTarget.dataset.readyLabel ?? ""
    }

    this.fade = setTimeout(() => {
      // Faded, then REMOVED. It is a one-shot: nothing on this page ever brings
      // it back, and a `hidden` overlay left in the tree is one more thing for
      // the next reader of this file to wonder about.
      this.element.style.opacity = "0"
      this.element.style.pointerEvents = "none"
      this.removal = setTimeout(() => this.element.remove(), FADE_MS)
    }, COMPLETION_MS)
  }
}

/**
 * v1's numbers, restated rather than imported.
 *
 * `loading-steps.ts` holds them, but importing it would make a controller in
 * the eager application chunk depend on a REACT component directory — and its
 * step keys and its `app/frontend/locales` text keys are both wrong for this
 * surface anyway. Two integers are not worth crossing that line; the point of
 * copying them is that the two editors feel the same.
 */
const MIN_STEP_MS = 600
const COMPLETION_MS = 500
const FADE_MS = 500

/**
 * The long stop. A number chosen, not measured — set it from a real p99 cold
 * load on a large floorplan when there is one to measure.
 */
const CEILING_MS = 20000
